import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { api, type Settings, type User } from './api'

/* ------------------------------------------------------------------- cart */

export type CartItem = { slug: string; qty: number }

const CART_KEY = 'lumera.cart.v1'

const MAX_QTY = 20

/** Two presses on the same product inside this window are one intent, not two. */
const REPEAT_MS = 900

/** How long the confirmation stays on screen. */
const TOAST_MS = 4200

function readCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(CART_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    // Storage is shared with the user and can be edited by hand, so every field
    // is re-checked. The server re-prices regardless; this just avoids crashes.
    return parsed
      .filter((i) => i && typeof i.slug === 'string' && Number.isFinite(i.qty))
      .map((i) => ({ slug: i.slug, qty: Math.min(20, Math.max(1, Math.trunc(i.qty))) }))
  } catch {
    return []
  }
}

/**
 * What the shopper is told after an add. `duplicate` is a second press on a
 * button they hit a moment ago — the cart is left exactly as it was.
 */
export type ToastKind = 'added' | 'updated' | 'duplicate'
export type CartToast = { id: number; title: string; detail: string; kind: ToastKind }

type CartCtx = {
  items: CartItem[]
  count: number
  add: (slug: string, qty?: number, name?: string) => void
  addMany: (entries: CartItem[], title: string) => void
  setQty: (slug: string, qty: number) => void
  remove: (slug: string) => void
  clear: () => void
  toast: CartToast | null
  dismissToast: () => void
}

const CartContext = createContext<CartCtx | null>(null)

/* --------------------------------------------------------------- settings */

type StoreCtx = {
  settings: Settings | null
  user: User | null
  userLoading: boolean
  setUser: (u: User | null) => void
  refreshUser: () => Promise<void>
  money: (n: number) => string
}

const StoreContext = createContext<StoreCtx | null>(null)

const FALLBACK_SYMBOL = '£'

export function StoreProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(readCart)
  const [settings, setSettings] = useState<Settings | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [userLoading, setUserLoading] = useState(true)

  useEffect(() => {
    try {
      localStorage.setItem(CART_KEY, JSON.stringify(items))
    } catch {
      // Private browsing can refuse writes; the in-memory cart still works.
    }
  }, [items])

  useEffect(() => {
    api.settings().then(setSettings).catch(() => setSettings(null))
  }, [])

  const refreshUser = useCallback(async () => {
    setUserLoading(true)
    try {
      const { user } = await api.me()
      setUser(user)
    } catch {
      setUser(null)
    } finally {
      setUserLoading(false)
    }
  }, [])

  useEffect(() => {
    void refreshUser()
  }, [refreshUser])

  const [toast, setToast] = useState<CartToast | null>(null)
  const toastTimer = useRef<number | null>(null)
  const lastAdd = useRef<{ slug: string; at: number } | null>(null)

  const showToast = useCallback((title: string, detail: string, kind: ToastKind) => {
    if (toastTimer.current) window.clearTimeout(toastTimer.current)
    setToast({ id: Date.now(), title, detail, kind })
    toastTimer.current = window.setTimeout(() => setToast(null), TOAST_MS)
  }, [])

  const dismissToast = useCallback(() => {
    if (toastTimer.current) window.clearTimeout(toastTimer.current)
    setToast(null)
  }, [])

  useEffect(
    () => () => {
      if (toastTimer.current) window.clearTimeout(toastTimer.current)
    },
    []
  )

  const cart = useMemo<CartCtx>(
    () => ({
      items,
      count: items.reduce((t, i) => t + i.qty, 0),
      add: (slug, qty = 1, name) => {
        const label = name?.trim() || 'Added'
        const existing = items.find((i) => i.slug === slug)
        const now = Date.now()
        const repeat = lastAdd.current?.slug === slug && now - lastAdd.current.at < REPEAT_MS
        lastAdd.current = { slug, at: now }

        // A second press inside the window is a double-click on one button: the
        // cart is left alone and the shopper is told it is already in there.
        if (repeat) {
          showToast(label, `Already in your bag — ${existing?.qty ?? qty} in total.`, 'duplicate')
          return
        }

        setItems((prev) => {
          const found = prev.find((i) => i.slug === slug)
          if (found) return prev.map((i) => (i.slug === slug ? { ...i, qty: Math.min(MAX_QTY, i.qty + qty) } : i))
          return [...prev, { slug, qty: Math.min(MAX_QTY, Math.max(1, qty)) }]
        })

        if (!existing) {
          showToast(label, qty > 1 ? `Added to your bag — quantity ${qty}.` : 'Added to your bag.', 'added')
          return
        }
        const next = Math.min(MAX_QTY, existing.qty + qty)
        showToast(
          label,
          next === existing.qty ? `Already in your bag — ${MAX_QTY} is the most we can hold.` : `Already in your bag — quantity now ${next}.`,
          'updated'
        )
      },
      addMany: (entries, title) => {
        setItems((prev) =>
          entries.reduce((acc, entry) => {
            const found = acc.find((i) => i.slug === entry.slug)
            if (found) return acc.map((i) => (i.slug === entry.slug ? { ...i, qty: Math.min(MAX_QTY, i.qty + entry.qty) } : i))
            return [...acc, { slug: entry.slug, qty: Math.min(MAX_QTY, Math.max(1, entry.qty)) }]
          }, prev)
        )
        lastAdd.current = null
        showToast(title, `${entries.length} product${entries.length === 1 ? '' : 's'} added to your bag.`, 'added')
      },
      setQty: (slug, qty) =>
        setItems((prev) =>
          qty <= 0 ? prev.filter((i) => i.slug !== slug) : prev.map((i) => (i.slug === slug ? { ...i, qty: Math.min(MAX_QTY, qty) } : i))
        ),
      remove: (slug) => setItems((prev) => prev.filter((i) => i.slug !== slug)),
      clear: () => setItems([]),
      toast,
      dismissToast,
    }),
    [items, toast, showToast, dismissToast]
  )

  const store = useMemo<StoreCtx>(
    () => ({
      settings,
      user,
      userLoading,
      setUser,
      refreshUser,
      money: (n: number) => `${settings?.currencySymbol ?? FALLBACK_SYMBOL}${Number(n ?? 0).toFixed(2)}`,
    }),
    [settings, user, userLoading, refreshUser]
  )

  return (
    <StoreContext.Provider value={store}>
      <CartContext.Provider value={cart}>{children}</CartContext.Provider>
    </StoreContext.Provider>
  )
}

export function useCart() {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be used inside StoreProvider')
  return ctx
}

export function useStore() {
  const ctx = useContext(StoreContext)
  if (!ctx) throw new Error('useStore must be used inside StoreProvider')
  return ctx
}

/** Small data-fetching helper: loading, error and refetch without a dependency. */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)
    fn()
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e?.message ?? 'Something went wrong.'))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce])

  return { data, error, loading, reload: () => setNonce((n) => n + 1) }
}
